// src/components/store/sections/ai-recommender/AiRecommenderFloatingCircle.tsx
"use client";

import { useState, useRef, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { 
  Sparkles, 
  ShoppingCart, 
  Zap, 
  Check, 
  ChevronLeft, 
  ChevronRight, 
  ShoppingBag, 
  Loader2,
  ArrowRight
} from "lucide-react";
import { useCart } from "@/context/CartContext";

interface ProductItem {
  id: number;
  name: string;
  slug: string;
  price: number | string;
  compare_price?: number | string | null;
  primary_image?: string | null;
  image?: string | null;
  description?: string;
  category_name?: string;
  unit?: string;
}

interface AiRecommenderProps {
  products?: ProductItem[];
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  subdomain?: string;
}

const CARD_THEMES = [
  {
    ringBorder: "border-[#f59e0b]",
    ringShadow: "shadow-amber-500/25",
    btnBg: "bg-[#f59e0b] hover:bg-[#d97706]",
    priceBg: "bg-[#fef3c7]",
    priceText: "text-[#b45309]",
  },
  {
    ringBorder: "border-[#f43f5e]",
    ringShadow: "shadow-rose-500/25",
    btnBg: "bg-[#f43f5e] hover:bg-[#e11d48]",
    priceBg: "bg-[#ffe4e6]",
    priceText: "text-[#be123c]",
  },
  {
    ringBorder: "border-[#06b6d4]",
    ringShadow: "shadow-cyan-500/25",
    btnBg: "bg-[#06b6d4] hover:bg-[#0891b2]",
    priceBg: "bg-[#cffafe]",
    priceText: "text-[#0e7490]",
  },
  {
    ringBorder: "border-[#8b5cf6]",
    ringShadow: "shadow-purple-500/25",
    btnBg: "bg-[#8b5cf6] hover:bg-[#7c3aed]",
    priceBg: "bg-[#ede9fe]",
    priceText: "text-[#6d28d9]",
  },
];

export default function AiRecommenderFloatingCircle({
  products = [],
  title = "Recommended For You",
  subtitle = "আপনার পছন্দ ও ট্রেন্ডিং আইটেমের ওপর ভিত্তি করে কৃত্রিম বুদ্ধিমত্তা চালিত বাছাই",
  primaryColor = "#059669",
  subdomain = "nextlook",
}: AiRecommenderProps) {
  const router = useRouter();
  const { addToCart } = useCart();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [addedIds, setAddedIds] = useState<Record<number, boolean>>({});
  const [buyingId, setBuyingId] = useState<number | null>(null);

  const resolveImageUrl = useCallback(
    (url?: string | null) => {
      if (!url) return null;
      const path = url.trim();
      if (path.startsWith("http://") || path.startsWith("https://")) return path;
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      const base = isLocal ? `http://${subdomain}.localhost:8000` : "";
      return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
    },
    [subdomain]
  );

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // 🌟 ADD TO CART বাটন হ্যান্ডলার
  const handleAddToCart = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setAddedIds((prev) => ({ ...prev, [prod.id]: true }));
    setTimeout(() => {
      setAddedIds((prev) => ({ ...prev, [prod.id]: false }));
    }, 2000);

    const fullImg = resolveImageUrl(prod.primary_image || prod.image);
    const itemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        let list: any[] = [];
        if (raw) {
          try {
            list = JSON.parse(raw);
          } catch {}
        }
        if (!Array.isArray(list)) list = [];
        const idx = list.findIndex((x: any) => String(x.id) === String(prod.id));
        if (idx > -1) {
          list[idx].quantity = (Number(list[idx].quantity) || 1) + 1;
        } else {
          list.push({ ...itemObj });
        }
        localStorage.setItem(key, JSON.stringify(list));
      } catch {}
    });

    if (typeof addToCart === "function") {
      addToCart(itemObj, 1);
    }

    window.dispatchEvent(new CustomEvent("addToCart", { detail: itemObj }));
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent("openCartDrawer"));
  };

  // 🌟 BUY NOW বাটন হ্যান্ডলার
  const handleBuyNow = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setBuyingId(prod.id);
    const fullImg = resolveImageUrl(prod.primary_image || prod.image);

    const singleItemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      title: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    try {
      sessionStorage.setItem("direct_checkout_item", JSON.stringify(singleItemObj));
    } catch {}

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        localStorage.setItem(key, JSON.stringify([singleItemObj]));
      } catch {}
    });

    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));

    router.push(`/checkout?buy_now=true&product_id=${prod.id}`);
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
      {/* 🌟 হেডার */}
      <div className="flex items-center justify-between mb-10 pb-3 border-b border-slate-100">
        <div>
          <span
            style={{ color: primaryColor }}
            className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono"
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" /> AI SMART RECOMMENDATIONS
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link
            href="/products"
            className="text-xs font-bold text-slate-600 hover:text-emerald-600 transition-colors hidden sm:flex items-center gap-1 mr-2"
          >
            <span>View All</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleScroll("left")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer hover:border-slate-300"
              aria-label="Scroll Left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleScroll("right")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer hover:border-slate-300"
              aria-label="Scroll Right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 🌟 হরিজন্টাল কার্ড কনটেইনার */}
      <div
        ref={scrollRef}
        className="flex items-stretch gap-6 sm:gap-8 overflow-x-auto pt-14 pb-8 px-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {products.map((item, idx) => {
          const theme = CARD_THEMES[idx % CARD_THEMES.length];
          const rawImg = item.primary_image || item.image;
          const imgUrl = resolveImageUrl(rawImg);
          const priceVal = typeof item.price === "number" ? item.price : parseFloat(String(item.price) || "0");
          const isAdded = addedIds[item.id];
          const isBuying = buyingId === item.id;
          const targetUrl = `/products/${item.slug || item.id}`;

          return (
            <div
              key={item.id}
              className="w-64 sm:w-72 shrink-0 group relative flex flex-col justify-between bg-white rounded-3xl border-2 border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 p-6 pt-16 text-center select-none"
            >
              {/* 🌟 ১. টপ ফ্লোটিং সার্কুলার ইমেজ ব্যাজ (ক্লিক করলে ডিটেইলস পেজে যাবে) */}
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-20">
                <Link href={targetUrl} className="block cursor-pointer">
                  <div
                    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 ${theme.ringBorder} bg-white shadow-lg ${theme.ringShadow} overflow-hidden p-1 flex items-center justify-center transition-transform duration-300 group-hover:scale-105`}
                  >
                    <div className="w-full h-full rounded-full overflow-hidden relative bg-slate-50 flex items-center justify-center">
                      {imgUrl ? (
                        <Image
                          src={imgUrl}
                          alt={item.name}
                          fill
                          unoptimized
                          className="object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <ShoppingBag className="w-8 h-8 text-slate-300" />
                      )}
                    </div>
                  </div>
                </Link>
              </div>

              {/* 🌟 ২. প্রোডাক্ট টাইটেল ও বিবরণ (ক্লিক করলে ডিটেইলস পেজে যাবে) */}
              <div className="space-y-1.5 mt-2">
                <Link href={targetUrl} className="block cursor-pointer">
                  <h3 className="text-sm sm:text-base font-black uppercase tracking-wider text-slate-800 hover:text-emerald-600 transition-colors line-clamp-1">
                    {item.name}
                  </h3>
                </Link>
                <p className="text-xs text-slate-400 font-medium line-clamp-2 px-2 leading-relaxed">
                  {item.description || "Write description product here, fresh and curated."}
                </p>
              </div>

              {/* 🌟 ৩. পেস্টেল রাউন্ড প্রাইস বাবল */}
              <div className="my-5 flex justify-center items-center">
                <div
                  className={`w-16 h-16 rounded-full ${theme.priceBg} flex items-center justify-center shadow-xs transition-transform duration-300 group-hover:scale-110`}
                >
                  <span className={`text-base sm:text-lg font-black tracking-tight ${theme.priceText}`}>
                    ৳{Math.round(priceVal).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* 🌟 ৪. অ্যাকশন বাটনসমূহ (Add to Cart & Buy Now) */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={(e) => handleAddToCart(e, item)}
                  className={`w-full py-3 px-4 rounded-full text-white text-xs font-black uppercase tracking-wider shadow-md transition-all duration-200 active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 ${
                    isAdded ? "bg-emerald-600 shadow-emerald-600/30" : theme.btnBg
                  }`}
                >
                  {isAdded ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>ADDED</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4" />
                      <span>ADD TO CART</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={(e) => handleBuyNow(e, item)}
                  className="w-full py-2.5 px-4 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-800 text-[11px] font-black uppercase tracking-wider transition-all duration-200 active:scale-95 cursor-pointer flex items-center justify-center gap-1.5"
                >
                  {isBuying ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-700" />
                  ) : (
                    <Zap className="w-3.5 h-3.5 fill-current text-amber-500" />
                  )}
                  <span>{isBuying ? "WAIT..." : "BUY NOW"}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}